import React from "react";
import { Check, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";

/**
 * Peças visuais do formulário de produto (layout de docs/tela/code.html).
 */

/** Card branco com borda quente e sombra suave */
export function SectionCard({ className, children }) {
  return (
    <div
      className={cn(
        "bg-card rounded-2xl border border-border shadow-soft",
        className
      )}
    >
      {children}
    </div>
  );
}

/** Rótulo pequeno em caixa alta usado acima dos campos */
export function FieldLabel({ htmlFor, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground"
    >
      {children}
    </label>
  );
}

export function FieldError({ error }) {
  if (!error?.message) return null;
  return <p className="text-xs text-destructive mt-1">{error.message}</p>;
}

/** Classe dos campos com fundo creme (foco: borda verde + anel pistache) */
export const fieldClass =
  "w-full bg-surface text-foreground rounded-xl px-3 py-2 text-[13px] border border-transparent placeholder:text-muted-foreground focus:border-brand focus:bg-card focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 disabled:opacity-60";

/** Aba do seletor segmentado */
export function TabButton({ active, icon: Icon, label, hasError, onClick }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "relative flex-1 py-2 px-2 sm:px-3 rounded-lg text-[13px] transition-all flex items-center justify-center gap-1.5",
        active
          ? "bg-brand text-white font-bold shadow-sm"
          : "text-muted-foreground font-medium hover:text-foreground"
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
      {hasError && (
        <span
          className="absolute top-1 right-1.5 h-2 w-2 rounded-full bg-destructive"
          aria-label="Campos com erro"
        />
      )}
    </button>
  );
}

/** Linha com ícone, título, descrição e switch */
export function ToggleRow({ id, icon: Icon, title, description, checked, onChange }) {
  return (
    <label
      htmlFor={id}
      className="flex items-center justify-between gap-3 py-2.5 px-1 cursor-pointer"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Icon className="h-5 w-5 text-brand flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-foreground">{title}</p>
          <p className="text-[11px] text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch id={id} checked={!!checked} onCheckedChange={onChange} />
    </label>
  );
}

/** Contador em pílula (ex.: "2 selecionados") */
export function CountBadge({ count, singular, plural }) {
  return (
    <span className="px-2.5 py-0.5 rounded-full bg-brand-pistachio/25 border border-brand-pistachio/50 text-brand text-[11px] font-bold whitespace-nowrap">
      {count} {count === 1 ? singular : plural}
    </span>
  );
}

export function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(fieldClass, "pl-9")}
      />
    </div>
  );
}

/** Título de grupo em caixa alta espaçada (category-subhead) */
export function GroupTitle({ title, description, count }) {
  return (
    <div className="flex items-end justify-between gap-2 px-1">
      <div>
        <h4 className="font-display text-[13px] font-bold uppercase tracking-[0.12em] text-brand">
          {title}
        </h4>
        {description && (
          <p className="text-[11px] text-muted-foreground">{description}</p>
        )}
      </div>
      {count > 0 && (
        <span className="text-[11px] font-semibold text-muted-foreground whitespace-nowrap">
          {count} marcado{count !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}

/**
 * Lista de seleção com checkbox por linha.
 * items: [{ id, name, hint?, disabled? }]; value: array de ids (string).
 * renderTag(item, checked) desenha o rótulo à direita (preço, "Incluso"...).
 */
export function SelectableList({
  items,
  value = [],
  onChange,
  renderTag,
  emptyText,
  idPrefix,
}) {
  const selected = new Set((value || []).map(String));

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange?.(Array.from(next));
  };

  if (!items.length) {
    return (
      <p className="text-[13px] text-muted-foreground text-center py-4 px-3 rounded-xl border border-dashed border-border">
        {emptyText}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {items.map((item) => {
        const checked = selected.has(item.id);
        const inputId = `${idPrefix}-${item.id}`;
        return (
          <li key={item.id}>
            <label
              htmlFor={inputId}
              className={cn(
                "bg-card p-3 rounded-xl border flex items-center justify-between gap-3 cursor-pointer transition-colors",
                checked
                  ? "border-brand/30 shadow-sm"
                  : "border-border hover:border-brand/20",
                item.disabled && !checked && "opacity-60"
              )}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <input
                  id={inputId}
                  type="checkbox"
                  className="peer sr-only"
                  checked={checked}
                  disabled={item.disabled && !checked}
                  onChange={() => toggle(item.id)}
                />
                <span
                  aria-hidden="true"
                  className={cn(
                    "h-5 w-5 flex-shrink-0 rounded-[5px] border flex items-center justify-center transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-brand/40",
                    checked
                      ? "bg-brand border-brand text-white"
                      : "bg-card border-[#c0c9bb]"
                  )}
                >
                  {checked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                </span>
                <div className="min-w-0">
                  <p
                    className={cn(
                      "text-[13px] text-foreground truncate",
                      checked ? "font-bold" : "font-medium"
                    )}
                  >
                    {item.name}
                  </p>
                  {item.hint && (
                    <span
                      className={cn(
                        "text-[11px]",
                        checked ? "text-brand font-medium" : "text-muted-foreground"
                      )}
                    >
                      {item.hint}
                    </span>
                  )}
                </div>
              </div>
              {renderTag?.(item, checked)}
            </label>
          </li>
        );
      })}
    </ul>
  );
}
